import { useId, useRef, useState, type ChangeEvent } from "react";
import { Button } from "../../components/ui/Button";
import { Checkbox } from "../../components/ui/Checkbox";
import { ProgressBar } from "../../components/ui/ProgressBar";
import { useI18n } from "../../i18n/I18nProvider";
import { playerApi, type PlayerPanelProps } from "./api";
import { errorMessageKey, uploadPhoto } from "./actions";

const PHOTO_ACCEPT = ".png,.jpg,.jpeg,image/png,image/jpeg";

function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className="size-4 shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  );
}

/** Player photo thumb with upload or replace. The first upload needs the consent box. */
export function PhotoPanel({ token, profile, onProfile, onFailure, onSuccess }: PlayerPanelProps) {
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const hintId = useId();
  const [consent, setConsent] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const locked = profile.locked;
  const photoId = profile.photoMediaId;
  const hasPhoto = photoId !== null;
  const frozen = profile.photoLocked || locked;
  const canUpload = !locked && progress === null && (hasPhoto || consent);
  const needsConsent = !frozen && !hasPhoto && !consent;
  const initial = profile.name.trim().slice(0, 1).toUpperCase();

  async function handleFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    setError(null);
    setProgress(0);
    try {
      const next = await uploadPhoto(token, file, { hasPhoto, consent, onProgress: setProgress });
      onProfile(next);
      onSuccess("player.photoSaved");
    } catch (caught: unknown) {
      setError(t(errorMessageKey(caught)));
      onFailure(caught);
    } finally {
      setProgress(null);
    }
  }

  const thumb =
    photoId !== null ? (
      <img
        src={playerApi.mediaUrl(token, photoId)}
        alt={t("player.photoAlt")}
        className="size-full object-cover"
      />
    ) : (
      <span
        aria-hidden="true"
        className="font-display text-3xl leading-none font-extrabold text-blue"
      >
        {initial}
      </span>
    );

  return (
    <section
      aria-labelledby="photo-heading"
      className="flex flex-col gap-4 rounded-panel bg-board p-4 shadow-board"
    >
      <h2
        id="photo-heading"
        className="font-display text-2xl leading-none font-extrabold uppercase"
      >
        {t("player.photo")}
      </h2>
      <div className="flex items-center gap-4">
        <div
          className={`flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-control ${hasPhoto ? "bg-ice-2" : "bg-blue-wash"}`}
        >
          {thumb}
        </div>
        {frozen ? (
          <p className="flex items-center gap-2 text-operate text-ink-2">
            <LockIcon />
            {t("player.photoLockedNote")}
          </p>
        ) : (
          <Button
            variant="secondary"
            disabled={!canUpload}
            aria-describedby={needsConsent ? hintId : undefined}
            onClick={() => inputRef.current?.click()}
          >
            {hasPhoto ? t("player.photoReplaceLabel") : t("player.photoUploadLabel")}
          </Button>
        )}
      </div>
      {needsConsent ? (
        <p id={hintId} className="text-note text-ink-3">
          {t("player.consentHint")}
        </p>
      ) : null}
      {!frozen && !hasPhoto ? (
        <Checkbox
          label={t("player.consent")}
          checked={consent}
          disabled={progress !== null}
          onChange={(event) => setConsent(event.target.checked)}
        />
      ) : null}
      {!profile.photoLocked ? (
        <input
          ref={inputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          className="hidden"
          aria-label={t("player.photoFile")}
          disabled={!canUpload}
          onChange={(event) => void handleFile(event)}
        />
      ) : null}
      {progress !== null ? (
        <ProgressBar
          fraction={progress}
          label={t("player.uploadingPhoto", { percent: Math.round(progress * 100) })}
        />
      ) : null}
      {error ? (
        <p role="alert" className="text-note font-medium text-red">
          {error}
        </p>
      ) : null}
    </section>
  );
}
